import { useEffect, useState } from "react";
import { toast } from "react-toastify";

import { useTranslation } from "../../i18n";
import { apiFetch } from "../../api/apiFetch";
import { evictPointsSettings } from "../gamification/pointsSettings";
import { useReadOnly } from "../../access/ReadOnly";
import "../../styles/admin-settings.css";
import "../../styles/points-admin.css";

interface GateRow {
    warehouse: string;
    trophy_open_interval_minutes: number;
    trophy_modal_seconds: number;
}

/**
 * Warehouse > Points > Settings.
 *
 * The trophy-modal gate: how often the modal can be opened (minutes from the
 * first scan of the day, restarting on every open — celebrations included)
 * and how long it stays up before closing itself. Per warehouse, like every
 * other points setting, so a floor can loosen or tighten its own rhythm.
 */
const PointsGateSettingsAdmin = () => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const [rows, setRows] = useState<GateRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [savingFor, setSavingFor] = useState<string | null>(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(`/settings`, {});
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data: GateRow[] = await res.json();
                if (alive) {
                    setRows(data.map(r => ({
                        warehouse: r.warehouse,
                        trophy_open_interval_minutes: r.trophy_open_interval_minutes ?? 120,
                        trophy_modal_seconds: r.trophy_modal_seconds ?? 20,
                    })));
                }
            } catch {
                toast.error(t("server_error"), { toastId: "points-gate-load-err" });
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [t]);

    const setField = (warehouse: string, field: keyof Omit<GateRow, "warehouse">, value: number) => {
        setRows(curr => curr.map(r => r.warehouse === warehouse ? { ...r, [field]: value } : r));
    };

    const save = async (row: GateRow) => {
        setSavingFor(row.warehouse);
        try {
            const res = await apiFetch(`/settings/${encodeURIComponent(row.warehouse)}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    trophy_open_interval_minutes: row.trophy_open_interval_minutes,
                    trophy_modal_seconds: row.trophy_modal_seconds,
                }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            evictPointsSettings(row.warehouse);
            toast.success(t("points_gate_saved"), { toastId: "points-gate-saved" });
        } catch {
            toast.error(t("server_error"), { toastId: "points-gate-save-err" });
        } finally {
            setSavingFor(null);
        }
    };

    if (loading) return <div className="locations-table-empty">{t("loading")}</div>;

    return (
        <div className="logs-admin users-admin">
            <div className="points-records-flow">{t("points_gate_title")}</div>
            <p className="admin-settings-section-hint">{t("points_gate_interval_hint")}</p>
            <p className="admin-settings-section-hint">{t("points_gate_seconds_hint")}</p>
            <div className="locations-table-wrap">
                <table className="locations-table">
                    <thead>
                        <tr>
                            <th>{t("logs_col_warehouse")}</th>
                            <th>{t("points_gate_interval_label")}</th>
                            <th>{t("points_gate_seconds_label")}</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(row => (
                            <tr key={row.warehouse}>
                                <td>{row.warehouse}</td>
                                <td>
                                    <input
                                        className="locations-table-search"
                                        style={{ width: 90 }}
                                        type="number"
                                        min={5}
                                        max={1440}
                                        value={row.trophy_open_interval_minutes}
                                        aria-label={`${row.warehouse} ${t("points_gate_interval_label")}`}
                                        onChange={e => setField(row.warehouse, "trophy_open_interval_minutes", Number(e.target.value))}
                                        disabled={readOnly}
                                    />
                                </td>
                                <td>
                                    <input
                                        className="locations-table-search"
                                        style={{ width: 90 }}
                                        type="number"
                                        min={5}
                                        max={300}
                                        value={row.trophy_modal_seconds}
                                        aria-label={`${row.warehouse} ${t("points_gate_seconds_label")}`}
                                        onChange={e => setField(row.warehouse, "trophy_modal_seconds", Number(e.target.value))}
                                        disabled={readOnly}
                                    />
                                </td>
                                <td>
                                    <button
                                        type="button"
                                        className="btn btn-primary dispatch-modal-btn"
                                        disabled={savingFor === row.warehouse || readOnly}
                                        aria-disabled={readOnly}
                                        title={readOnly ? t("read_only_mode") : undefined}
                                        onClick={() => void save(row)}
                                    >
                                        {t("save")}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default PointsGateSettingsAdmin;
